import type { ReportBlockData } from '../../services/report-blocks.service';

export type ReportRunId = string;
export type SlideId = string;
export type WidgetId = string;

export type SlideType =
	| 'cover_page'
	| 'executive_summary'
	| 'volume_of_mentions_reach'
	| 'sentiment_over_time'
	| 'emotion_over_time'
	| 'sentiment_by_media_type'
	| 'emotion_analysis'
	| 'top_influencers_by_voice_share'
	| 'top_sites_by_visits'
	| 'influencers_by_media_type'
	| 'best_time_to_post'
	| 'website_traffic_demographics'
	| 'top_countries'
	| 'trending_hashtags'
	| 'trending_conversations'
	| 'mentions_by_media_type'
	| 'languages'
	/** Built by the AI builder from the block catalogue, not one of the
	    sixteen fixed sections. */
	| 'custom_block'
	/** A text block the reader wrote, as the slide-edit agent is handed it
	    (`textBlockEditSlide`). Never drawn as a slide. */
	| 'text_block';

export type WidgetKind =
	| 'title'
	| 'subtitle'
	| 'summary_text'
	| 'kpi'
	| 'chart'
	| 'table'
	| 'note'
	| 'date_range';

export type ChartType =
	| 'line'
	| 'area'
	| 'bar'
	| 'stacked_bar'
	| 'pie'
	| 'doughnut'
	| 'polarArea'
	| 'matrix'
	| 'wordCloud';

export interface ReportPreviewDocument {
	runId: ReportRunId;
	reportId: string;
	templateVersion: number;
	generatedAt: string;
	slides: PreviewSlide[];
}

export interface PreviewSlide {
	id: SlideId;
	slideType: SlideType;
	title: string;
	description?: string;
	dateRange?: { start: string; end: string; label: string };
	widgets: PreviewWidget[];
	allowedChartTypes?: ChartType[];
	allowedWidgetKinds?: WidgetKind[];
	overrides?: SlideOverrides;
	/**
	 * The section's chart numbers, in the form an added chart arrives in
	 * (`sectionChartSource`) — what a number in its text is checked against.
	 * Put on by the slide host as the slide is announced; the editor keeps
	 * them apart from the slide it stores, so nothing that reads a stored
	 * slide (the slide-edit agent, the colour palette) carries them.
	 */
	chartNumbers?: ReportBlockData | null;
}

interface PreviewWidgetBase {
	id: WidgetId;
	kind: WidgetKind;
	visible: boolean;
	/**
	 * The reader typed this widget's text themselves. Set from the saved
	 * override when the slide is built, and sent with the slide to the
	 * slide-edit agent, which leaves such text alone unless a request is aimed
	 * at it.
	 */
	editedByUser?: boolean;
}

export interface TitleWidget extends PreviewWidgetBase {
	kind: 'title' | 'subtitle' | 'date_range';
	content: { text: string; color?: string };
}

export interface SummaryTextWidget extends PreviewWidgetBase {
	kind: 'summary_text' | 'note';
	content: { text: string };
}

export interface KpiWidget extends PreviewWidgetBase {
	kind: 'kpi';
	content: {
		label: string;
		value: string;
		/**
		 * The number `value` shows, unrounded: "1.3K" is 1,340 here. What a
		 * number in the section's text is compared with, so a figure is never
		 * read back out of its display string.
		 */
		amount?: number;
		/** `%` when `amount` is a share, in percentage points. */
		unit?: '%';
		delta?: string;
		legendColor?: string;
		icon?: string;
		sparkline?: number[];
		// Qualitative status (e.g. Brand Reputation "Good"). Shown on cards that
		// have no sparkline so they aren't left empty. Structural match for
		// AnalyticsBadge.
		badge?: { type: 'good' | 'medium' | 'bad'; text: string } | null;
	};
}

export interface ChartWidget extends PreviewWidgetBase {
	kind: 'chart';
	content: {
		chartType: ChartType;
		dataSourceKey: string;
		seriesMeta?: Array<{ key: string; label: string; color?: string }>;
		chartJsConfig: Record<string, unknown>;
	};
}

export interface TableWidget extends PreviewWidgetBase {
	kind: 'table';
	content: {
		dataSourceKey: string;
		accentColor?: string;
		columns: Array<{
			key: string;
			label: string;
			/** `number` right-aligns and formats. Text is left alone — a handle
			    like `@2024news` is not a number however much it looks like one. */
			kind?: 'text' | 'number';
		}>;
		rows: Array<Record<string, string>>;
		colorScale?: {
			low: string;
			midLow: string;
			midHigh: string;
			high: string;
		};
	};
}

export type PreviewWidget =
	| TitleWidget
	| SummaryTextWidget
	| KpiWidget
	| ChartWidget
	| TableWidget;

export interface SlideOverrides {
	hiddenWidgetIds?: WidgetId[];
}

export interface ReportDateRange {
	start: string;
	end: string;
	label: string;
	/**
	 * The period key the window was built from (`last_7_days`, `all_time`…).
	 *
	 * Carried with the instants so whatever describes the window can say it
	 * the way the reader chose it — "in the last 7 days", "All time" — rather
	 * than reverse-engineering a period out of two timestamps. Optional: a
	 * range built anywhere but `buildPreviewDateRange` has none, and every
	 * reader of it falls back to the instants.
	 */
	period?: string;
}

/**
 * How a block that covers several entities encodes them, as the backend writes
 * it (see `reports/blocks/comparison.py`).
 *
 * Here, in the models, because the widgets that read it are presentational and
 * the file they used to import it from pulls in an HTTP service and the auth
 * interceptor. The fixed slides render through the same table widget, so that
 * import put a data service into their module graph for the sake of one string.
 */
export const ENTITY_COLUMN_KEY = 'entity';
export const ENTITY_FIGURE_SEPARATOR = ' · ';
